import { change, DecoratedFormProps, FieldArray, FormErrors, getFormValues, InjectedFormProps, reduxForm } from 'redux-form';
import { FormError } from 'src/components/redux-form/FormError';
import React, { Dispatch, useEffect, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { FieldInput } from 'src/components/redux-form/fields/FieldInput';
import { Prompt, useHistory } from 'react-router-dom';
import Router from 'src/navigation/router';
import { routes } from 'src/navigation';
import styles from './assets/program-book-form.module.scss';
import { faSave, faBan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { asyncPersistProgramBook } from 'src/store/actions/program-book-actions';
import Loader from 'src/components/Loader';
import { ProgramBookApi } from 'src/services/end-points';
import { groupProgramBook } from 'src/helpers/programBook';
import { DropZoneField } from '../../../../../components/DropZoneField';
import RenderSectionArray from './RenderSectionArray';
import { Button } from 'react-bootstrap';
import { DndProvider } from './dnd/DndRegistry';

export interface ProgramBookFormData {
    id: string;
    studentId: string;
    values: any;
    title: string;
    cpt: string;
    isEvaluation: boolean;
}
interface ProgramBookFormProps {
    handleSub: (data: (e: any) => void, e: any) => void;
    files: any;
    miniLoaderFor: any;
    removeFile: (id: any) => void;
    setFiles: (it: []) => void;
}

type InjectedProps = InjectedFormProps<ProgramBookFormData, ProgramBookFormProps>;

const ProgramBookForm: React.FunctionComponent<InjectedProps & ProgramBookFormProps> = ({
    handleSubmit,
    error,
    dirty,
    handleSub,
    miniLoaderFor,
    removeFile,

    submitting,
    initialValues,
    files,
    setFiles,
}: InjectedProps & ProgramBookFormProps) => {
    const isNew = !initialValues || !initialValues.id;
    const dispatch = useDispatch();
    const history = useHistory();
    const formValues = useSelector(getFormValues('program-book'));
    const formValuesRef = useRef<any>(null);
    formValuesRef.current = formValues;

    const isNewRef = useRef(isNew);
    isNewRef.current = isNew;

    const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [isAutoSaving, setIsAutoSaving] = useState(false);
    const [isLeaving, setIsLeaving] = useState(false);
    const [hasPendingChanges, setHasPendingChanges] = useState(false);
    const hasPendingChangesRef = useRef(false);
    const isAutoSavingRef = useRef(false);
    const isInitialMount = useRef(true);
    const isSyncingIdsRef = useRef(false);
    const isManualSavingRef = useRef(false);

    // Check whether the target is ready for auto-save
    // Existing (with an id) and deleted ones are always ready.
    // New ones only if all required fields are filled in.
    const isTargetReadyForAutoSave = (target: any): boolean => {
        if (target?.id) return true;
        if (+target?.status === 2) return true;

        const isEmpty = (v: any) => v === '' || v === undefined || v === null;

        // title and type are always required
        if (!target?.title?.trim()) return false;
        const typeVal = target?.type?.value ?? target?.type;
        if (!typeVal) return false;

        // "Need phases?" must be answered
        const isPhased = target?.isPhased;
        if (isPhased === undefined || isPhased === null) return false;

        if (+isPhased === 1) {
            // Phased: check every phase
            const phases = target?.programTargetPhases || [];
            if (phases.length === 0) return false;
            for (const phase of phases) {
                if (+phase?.status === 2) continue;
                if (!phase?.title?.trim()) return false;
                if (isEmpty(phase?.startDate)) return false;
                if (isEmpty(phase?.masterValue)) return false;
                if (!phase?.masteryDirection) return false;
                if (isEmpty(phase?.masterNumberOfSeance)) return false;
            }
            return true;
        }

        // Not phased: mastery fields are required
        if (isEmpty(target?.masterValue)) return false;
        if (!target?.masteryDirection) return false;
        if (isEmpty(target?.masterNumberOfSeance)) return false;

        return true;
    };

    // Filters form values for auto-save:
    // - Drops incomplete targets (isTargetReadyForAutoSave)
    // - Protects phases: if there is a new phase without an ID and every existing one has isComplete=1,
    //   roll back isComplete on the last one → the server always sees exactly 1 active phase.
    //   Once the new phase gets an ID via syncIds, the next auto-save sends isComplete=1.
    const protectPhasesForAutoSave = (target: any) => {
        const phases: any[] = target.programTargetPhases || [];
        const withId = phases.filter((p: any) => !!p?.id);
        const hasNew = phases.length > withId.length;
        if (hasNew && withId.length > 0 && withId.every((p: any) => +p.isComplete === 1)) {
            const lastId = withId[withId.length - 1]?.id;
            return {
                ...target,
                programTargetPhases: phases.map((p: any) =>
                    p?.id === lastId ? { ...p, isComplete: 0 } : p,
                ),
            };
        }
        return target;
    };

    const filterForAutoSave = (formVals: any): any => ({
        ...formVals,
        values: (formVals.values || []).map((section: any) => ({
            ...section,
            values: (section.values || []).map((domain: any) => ({
                ...domain,
                values: (domain.values || []).map((lt: any) => ({
                    ...lt,
                    values: (lt.values || []).map((st: any) => ({
                        ...st,
                        targets: (st.targets || [])
                            .filter((t: any) => isTargetReadyForAutoSave(t))
                            .map(protectPhasesForAutoSave),
                    })),
                })),
            })),
        })),
    });

    // Check whether there are items without an ID (new, awaiting save)
    // Checks every level: categories, targets, phases, options
    const hasElementsWithoutId = (formVals: any): boolean => {
        for (const section of formVals?.values || []) {
            if (+section?.status === 2) continue;
            if (!section?.id) return true;
            for (const domain of section?.values || []) {
                if (+domain?.status === 2) continue;
                if (!domain?.id) return true;
                for (const lt of domain?.values || []) {
                    if (+lt?.status === 2) continue;
                    if (!lt?.id) return true;
                    for (const st of lt?.values || []) {
                        if (+st?.status === 2) continue;
                        if (!st?.id) return true;
                        for (const target of st?.targets || []) {
                            if (+target?.status === 2) continue;
                            if (!isTargetReadyForAutoSave(target)) continue;
                            if (!target?.id) return true;
                            // Check the phases inside the target
                            for (const phase of target?.programTargetPhases || []) {
                                if (+phase?.status === 2) continue;
                                // A new phase reaches the payload only if it has title and startDate
                                if (!phase?.id && phase?.startDate && phase?.title) return true;
                                // Check the phase options
                                for (const phaseOpt of phase?.programTargetPhaseOptions || []) {
                                    if (+phaseOpt?.status === 2) continue;
                                    if (!phaseOpt?.id && phaseOpt?.title) return true;
                                }
                            }
                            // Check the target options
                            for (const opt of target?.programTargetOptions || []) {
                                if (+opt?.status === 2) continue;
                                if (!opt?.id && opt?.title) return true;
                            }
                        }
                    }
                }
            }
        }
        return false;
    };

    // Sync IDs from GET response to form (prevents duplication on next auto-save)
    const awaitingIdsRef = useRef(false);

    const syncIdsFromResponse = (responseData: any) => {
        try {
            const currentFormValues = formValuesRef.current;
            const changes: Array<[string, any]> = [];
            const toArr = (v: any): any[] => {
                if (!v) return [];
                if (Array.isArray(v)) return v;
                if (typeof v === 'object') return Object.values(v);
                return [];
            };

            const getChildCategories = (item: any): any[] =>
                toArr(item.programCategories || item.values);
            const getChildTargets = (item: any): any[] =>
                toArr(item.programTargets || item.targets);

            // Matches existing items by ID; assigns an ID to new ones
            // Sorted by childCount DESC so duplicates match the item that has children
            const matchLevel = (
                serverItems: any[],
                formItems: any[],
                pathPrefix: string,
            ): Map<number, any> => {
                const matched = new Map<number, any>();

                const formIds = new Set<string>();
                formItems.forEach((f: any) => {
                    if (f?.id) formIds.add(String(f.id));
                });

                const childCount = (s: any) =>
                    getChildCategories(s).length + getChildTargets(s).length;

                const newServerItems = serverItems
                    .filter((s: any) => s?.id && !formIds.has(String(s.id)))
                    .sort((a: any, b: any) => childCount(b) - childCount(a));

                let newIdx = 0;
                formItems.forEach((fItem: any, i: number) => {
                    if (fItem?.id) {
                        const sItem = serverItems.find(
                            (s: any) => s?.id && String(s.id) === String(fItem.id),
                        );
                        if (sItem) matched.set(i, sItem);
                    } else {
                        if (newIdx < newServerItems.length) {
                            const sItem = newServerItems[newIdx++];
                            changes.push([`${pathPrefix}[${i}].id`, sItem.id]);
                            matched.set(i, sItem);
                        }
                    }
                });

                return matched;
            };

            const serverSections = toArr(
                responseData?.programMainCategories || responseData?.categories,
            );
            const formSections = currentFormValues?.values || [];

            const sectionMatches = matchLevel(serverSections, formSections, 'values');

            sectionMatches.forEach((sSection, i) => {
                const fSection = formSections[i];
                const domainMatches = matchLevel(
                    getChildCategories(sSection), fSection.values || [], `values[${i}].values`,
                );

                domainMatches.forEach((sDomain, j) => {
                    const fDomain = (fSection.values || [])[j];
                    const ltMatches = matchLevel(
                        getChildCategories(sDomain), fDomain.values || [], `values[${i}].values[${j}].values`,
                    );

                    ltMatches.forEach((sLT, k) => {
                        const fLT = (fDomain.values || [])[k];
                        const stPath = `values[${i}].values[${j}].values[${k}]`;
                        const stMatches = matchLevel(
                            getChildCategories(sLT), fLT.values || [], `${stPath}.values`,
                        );

                        stMatches.forEach((sST, l) => {
                            const fST = (fLT.values || [])[l];
                            const stFullPath = `${stPath}.values[${l}]`;
                            const targetMatches = matchLevel(
                                getChildTargets(sST), fST.targets || [], `${stFullPath}.targets`,
                            );

                            targetMatches.forEach((sTarget, m) => {
                                const fTarget = (fST.targets || [])[m];
                                const tPath = `${stFullPath}.targets[${m}]`;

                                matchLevel(
                                    toArr(sTarget.programTargetOptions),
                                    fTarget.programTargetOptions || [],
                                    `${tPath}.programTargetOptions`,
                                );
                                matchLevel(
                                    toArr(sTarget.programTargetPrompts),
                                    fTarget.programTargetPrompts || [],
                                    `${tPath}.programTargetPrompts`,
                                );
                                const phaseMatches = matchLevel(
                                    toArr(sTarget.programTargetPhases),
                                    fTarget.programTargetPhases || [],
                                    `${tPath}.programTargetPhases`,
                                );
                                phaseMatches.forEach((sPhase, n) => {
                                    const fPhase = (fTarget.programTargetPhases || [])[n];
                                    if (fPhase) {
                                        matchLevel(
                                            toArr(sPhase?.programTargetPhaseOptions),
                                            fPhase?.programTargetPhaseOptions || [],
                                            `${tPath}.programTargetPhases[${n}].programTargetPhaseOptions`,
                                        );
                                    }
                                });
                            });
                        });
                    });
                });
            });

            if (changes.length > 0) {
                isSyncingIdsRef.current = true;
                batch(() => {
                    changes.forEach(([field, value]) => {
                        dispatch(change('program-book', field, value));
                    });
                });
                setTimeout(() => {
                    isSyncingIdsRef.current = false;
                    awaitingIdsRef.current = false;
                    // After the ID sync run one more auto-save —
                    // the server created the items with sortOrder=0,
                    // so now send an update with the correct sortOrder
                    triggerAutoSave();
                }, 300);
            } else {
                awaitingIdsRef.current = false;
            }
        } catch (err) {
            console.error('[auto-save] syncIds ERROR:', err);
            awaitingIdsRef.current = false;
        }
    };

    // Core auto-save logic
    const autoSavePromiseRef = useRef<Promise<void> | null>(null);

    const triggerAutoSave = () => {
        if (isAutoSavingRef.current || !formValuesRef.current) return;

        // Block auto-save while a manual save (Update) is running
        if (isManualSavingRef.current) return;

        // Duplicate guard: wait for the IDs from the previous save
        if (awaitingIdsRef.current) {
            return;
        }

        // Filter out new targets whose required fields are not filled in
        const filtered = filterForAutoSave(formValuesRef.current);
        const willHaveNewElements = hasElementsWithoutId(filtered);

        isAutoSavingRef.current = true;
        setIsAutoSaving(true);

        if (willHaveNewElements) {
            awaitingIdsRef.current = true;
        }

        const { id, ...restData } = filtered;
        const grouped = groupProgramBook(restData);

        const autoSavePayload = {
            student: grouped.student,
            categories: grouped.categories,
        };

        const promise = ProgramBookApi.autoSaveProgramBook(id, autoSavePayload)
            .then(() => {
                if (willHaveNewElements) {
                    // The PUT response does not include nested children for new items.
                    // Do a GET to fetch the full tree with all IDs.
                    return ProgramBookApi.getProgramBookEntity(id, '1').then((getResponse) => {
                        syncIdsFromResponse(getResponse.data);
                    });
                }
                // No new items — no sync needed
            })
            .catch((err) => {
                console.error('[auto-save] error:', err);
                awaitingIdsRef.current = false;
            })
            .finally(() => {
                isAutoSavingRef.current = false;
                setIsAutoSaving(false);
                setHasPendingChanges(false);
                hasPendingChangesRef.current = false;
                autoSavePromiseRef.current = null;
            });

        autoSavePromiseRef.current = promise;
    };

    // Auto-save: watch formValues (catches every change: inputs, dropdowns, checkboxes, DnD)
    // Call the API directly, bypassing the saga, so the form is not reloaded
    useEffect(() => {
        if (isNew || !formValues) return;

        // Skip the first render (form initialization)
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);

        // Skip auto-save if the change came from the ID sync
        // (the flag is cleared on a timeout in syncIdsFromResponse, not here)
        if (isSyncingIdsRef.current) {
            return;
        }

        // Do not run auto-save while a manual save is in flight or the form is re-initializing
        if (isManualSavingRef.current || submitting) {
            return;
        }

        // Do not run auto-save for a pristine form (after re-initialization)
        if (!dirty) {
            setHasPendingChanges(false);
            hasPendingChangesRef.current = false;
            return;
        }

        setHasPendingChanges(true);
        hasPendingChangesRef.current = true;

        autoSaveTimer.current = setTimeout(() => {
            triggerAutoSave();
        }, 3000);

        return () => {
            if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
        };
    }, [formValues, isNew, submitting, dirty]);

    // Clear the manual-save flag once submit has finished
    // The delay suppresses auto-save while the form re-initializes
    useEffect(() => {
        if (!submitting && isManualSavingRef.current) {
            const timer = setTimeout(() => {
                isManualSavingRef.current = false;
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [submitting]);

    // Flush auto-save on unmount (fire-and-forget)
    useEffect(() => {
        return () => {
            if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);

            const vals = formValuesRef.current;
            if (!vals || isNewRef.current) return;
            if (!hasPendingChangesRef.current) return;
            // If an auto-save is already in flight it will finish on its own
            if (isAutoSavingRef.current) return;

            const filtered = filterForAutoSave(vals);
            const { id, ...restData } = filtered;
            const grouped = groupProgramBook(restData);

            ProgramBookApi.autoSaveProgramBook(id, {
                student: grouped.student,
                categories: grouped.categories,
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            }).catch(() => {});
        };
        // eslint-disable-next-line
    }, []);

    // beforeunload — warn on tab close / refresh
    useEffect(() => {
        if (isNew || !hasPendingChanges) return;

        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [hasPendingChanges, isNew]);

    const [isHidden, setIsHidden] = useState('0');

    return (
        <DndProvider>
            <Prompt
                when={!isNew && hasPendingChanges && !isLeaving}
                message="You have unsaved changes. Are you sure you want to leave?"
            />
            <form onSubmit={handleSubmit} className={classNames(styles.form)}>
                <FormError error={error} />
                <FieldInput
                    name="id"
                    type="hidden"
                    wrapperConfig={{
                        classes: {
                            inputWrapper: 'm-0',
                        },
                    }}
                />
                <FieldInput
                    name="studentId"
                    type="hidden"
                    wrapperConfig={{
                        classes: {
                            inputWrapper: 'm-0',
                        },
                    }}
                />

                <div>
                    {isHidden == '0' ? (
                        <Button
                            className={'mr-2'}
                            variant="secondary"
                            onClick={() => {
                                setIsHidden('1');
                            }}
                        >
                            Click here to see active and hidden
                        </Button>
                    ) : (
                        <Button
                            className={'mr-2'}
                            variant="secondary"
                            onClick={() => {
                                setIsHidden('0');
                            }}
                        >
                            Click here to see active only
                        </Button>
                    )}
                </div>

                <FieldArray name="values" isHiddenShow={isHidden} component={RenderSectionArray} />

                <div className={'position-relative'}>
                    {miniLoaderFor['FILE'] ? <Loader noFull={true} /> : null}
                    <div className={classNames(styles.mTop, styles.drop50)}>
                        <div>Document</div>
                        <DropZoneField
                            typeDropZone={'main'}
                            typeViewFiles={'forService'}
                            download={true}
                            removeFile={(id: string | number) => removeFile(id)}
                            single={true}
                            files={files}
                            setFiles={setFiles}
                        />
                    </div>
                </div>

                <FieldInput
                    name="isEvaluation"
                    type="hidden"
                    wrapperConfig={{
                        label: '',
                        classes: {
                            inputWrapper: 'customCheckbox',
                        },
                    }}
                />
                <div className={classNames(styles.actions)}>
                    <button
                        type="button"
                        onClick={async (e) => {
                            // Cancel the pending auto-save timer
                            if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);

                            // If an auto-save is in flight — wait for it to finish
                            // so syncIds can assign IDs to the new items
                            if (isAutoSavingRef.current && autoSavePromiseRef.current) {
                                try {
                                    await autoSavePromiseRef.current;
                                } catch {}
                            }

                            isManualSavingRef.current = true;
                            handleSub(handleSubmit, e);
                        }}
                        className={classNames('btn', 'btn-success', 'mr-3')}
                        disabled={!dirty || submitting || isAutoSaving}
                    >
                        <FontAwesomeIcon icon={faSave} /> {isNew ? 'Create' : 'Update'}
                    </button>
                    <button
                        type="button"
                        className={classNames('btn', 'btn-danger')}
                        onClick={() => {
                            if (!hasPendingChanges || window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
                                setIsLeaving(true);
                                setTimeout(() => history.push(Router.generate(routes.PROVIDERS_LIST)), 0);
                            }
                        }}
                    >
                        <FontAwesomeIcon icon={faBan} /> Cancel
                    </button>
                </div>
            </form>
        </DndProvider>
    );
};
export default reduxForm<ProgramBookFormData, ProgramBookFormProps>({
    form: 'program-book',
    enableReinitialize: true,
    validate(
        values: ProgramBookFormData,
        props: DecoratedFormProps<ProgramBookFormData, ProgramBookFormProps, string>,
    ): FormErrors<ProgramBookFormData, string> {
        const errors: FormErrors<ProgramBookFormData, string> = {};
        return errors;
    },
    onSubmit: async (data: ProgramBookFormData, dispatch: Dispatch<any>, props: ProgramBookFormProps) => {
        await asyncPersistProgramBook(data, { files: props.files }, dispatch);
    },
    onSubmitSuccess(
        result: any,
        dispatch: Dispatch<any>,
        props: DecoratedFormProps<ProgramBookFormData, ProgramBookFormProps, string>,
    ) {
        const isNew = !props.initialValues || !props.initialValues.id;
        toast.success(`Program Book was successfully ${isNew ? 'created' : 'updated'}.`);
    },
    onSubmitFail(errors: any) {
        console.error('ProgramBook submit errors:', errors);
        if (errors?._error) {
            toast.error(errors._error);
        } else if (errors) {
            toast.error('Validation error. Please check the form fields.');
        }

        // Scroll to first field with error
        const findFirstErrorPath = (obj: any, path = ''): string | null => {
            if (!obj || typeof obj !== 'object') return null;
            for (const key of Object.keys(obj)) {
                if (key === '_error') continue;
                const val = obj[key];
                const fullPath = path ? `${path}.${key}` : key;
                if (typeof val === 'string') return fullPath;
                if (Array.isArray(val)) {
                    for (let i = 0; i < val.length; i++) {
                        if (val[i]) {
                            const found = findFirstErrorPath(val[i], `${fullPath}[${i}]`);
                            if (found) return found;
                        }
                    }
                } else if (typeof val === 'object') {
                    const found = findFirstErrorPath(val, fullPath);
                    if (found) return found;
                }
            }
            return null;
        };

        const fieldName = findFirstErrorPath(errors);
        if (fieldName) {
            setTimeout(() => {
                const el = document.querySelector(`[name="${fieldName}"]`) as HTMLElement;
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    el.focus();
                } else {
                    // Dropdowns/datepickers don't render name attr on their DOM element,
                    // but FieldWrapper renders <label htmlFor={name}> — use it as fallback
                    const label = document.querySelector(`label[for="${fieldName}"]`) as HTMLElement;
                    const wrapper = (label?.closest('.form-group') || label) as HTMLElement;
                    if (wrapper) {
                        wrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }
            }, 100);
        }
    },
})(ProgramBookForm);
